/**
 * Adapted from: Kami
 * Source: https://github.com/tw93/Kami
 * Source commit: 594bfb8f2c62f26102a377c2e6cdb8cafa7d61f1
 * License: MIT
 * Modified for: Smart Shikka Core PR79 selective source adoption.
 * Safety: metadata/preview only; no runtime execution.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SOURCE_REPO_URL = "https://github.com/tw93/Kami";
const SOURCE_COMMIT_SHA = "594bfb8f2c62f26102a377c2e6cdb8cafa7d61f1";
const SOURCE_LICENSE = "MIT";
const COMMON_BLOCKED_ACTIONS = deepFreeze([
    "run_source_repo_script",
    "install_source_repo_dependencies",
    "execute_renderer",
    "network_call",
    "browser_automation",
    "desktop_automation",
    "read_secret",
    "db_write",
    "supabase_call",
    "provider_call",
    "copy_full_repo",
    "vendor_full_repo",
    "copy_forbidden_fonts",
    "copy_forbidden_scripts",
    "copy_pake_source",
    "run_pake_command",
    "auto_save",
    "auto_delete",
    "payment_trigger",
    "role_mutation",
    "secret_mutation",
]);
// ---------------------------------------------------------------------------
// Template definitions
// ---------------------------------------------------------------------------
const KAMI_TEMPLATES = deepFreeze([
    {
        id: "changelog",
        displayName: "Smart Shikka AI document template: Changelog",
        fileName: "changelog.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/changelog.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami changelog template. Static preview only."],
    },
    {
        id: "letter",
        displayName: "Smart Shikka AI document template: Letter",
        fileName: "letter.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/letter.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami letter template. Static preview only."],
    },
    {
        id: "long-doc",
        displayName: "Smart Shikka AI document template: Long Document",
        fileName: "long-doc.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/long-doc.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami long-doc template. Static preview only."],
    },
    {
        id: "one-pager",
        displayName: "Smart Shikka AI document template: One-Pager",
        fileName: "one-pager.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/one-pager.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami one-pager template. Static preview only."],
    },
    {
        id: "portfolio",
        displayName: "Smart Shikka AI document template: Portfolio",
        fileName: "portfolio.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/portfolio.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami portfolio template. Static preview only."],
    },
    {
        id: "resume",
        displayName: "Smart Shikka AI document template: Resume",
        fileName: "resume.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/resume.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami resume template. Static preview only."],
    },
    {
        id: "slides-weasy",
        displayName: "Smart Shikka AI document template: Slides (WeasyPrint)",
        fileName: "slides-weasy.html",
        relativePath: "packages/core/src/kami-document-renderer/templates/slides-weasy.html",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        previewOnly: true,
        runtimeRenderingAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        notes: ["Adapted from Kami slides-weasy template. Static preview only. WeasyPrint is NOT executed."],
    },
]);
// ---------------------------------------------------------------------------
// Reference definitions
// ---------------------------------------------------------------------------
const KAMI_REFERENCES = deepFreeze([
    {
        id: "design",
        displayName: "Smart Shikka AI document renderer reference: Design",
        fileName: "design.md",
        relativePath: "packages/core/src/kami-document-renderer/references/design.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
    },
    {
        id: "writing",
        displayName: "Smart Shikka AI document renderer reference: Writing",
        fileName: "writing.md",
        relativePath: "packages/core/src/kami-document-renderer/references/writing.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
    },
    {
        id: "production",
        displayName: "Smart Shikka AI document renderer reference: Production",
        fileName: "production.md",
        relativePath: "packages/core/src/kami-document-renderer/references/production.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
    },
    {
        id: "tokens",
        displayName: "Smart Shikka AI document renderer reference: Tokens",
        fileName: "tokens.json",
        relativePath: "packages/core/src/kami-document-renderer/references/tokens.json",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
    },
    {
        id: "checks_thresholds",
        displayName: "Smart Shikka AI document renderer reference: Checks Thresholds",
        fileName: "checks_thresholds.json",
        relativePath: "packages/core/src/kami-document-renderer/references/checks_thresholds.json",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
    },
]);
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const KAMI_DOCUMENT_RENDERER_REGISTRY = deepFreeze({
    version: "1.0.0",
    generatedInPr: 79,
    sourceRepoUrl: SOURCE_REPO_URL,
    sourceCommitSha: SOURCE_COMMIT_SHA,
    sourceLicense: SOURCE_LICENSE,
    sourceLicenseVerified: true,
    templates: KAMI_TEMPLATES,
    references: KAMI_REFERENCES,
});
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function getKamiDocumentTemplates() {
    return KAMI_DOCUMENT_RENDERER_REGISTRY.templates;
}
export function getKamiDocumentTemplateById(id) {
    return KAMI_DOCUMENT_RENDERER_REGISTRY.templates.find((t) => t.id === id);
}
export function getKamiDocumentReferences() {
    return KAMI_DOCUMENT_RENDERER_REGISTRY.references;
}
export function getKamiDocumentReferenceById(id) {
    return KAMI_DOCUMENT_RENDERER_REGISTRY.references.find((r) => r.id === id);
}
//# sourceMappingURL=kamiDocumentTemplateRegistry.js.map